import { ref, reactive } from 'vue'

// État global pour les toasts
const toasts = reactive([])
const toastIdCounter = ref(0)

/**
 * Composable pour la gestion des notifications toast
 * @returns {Object} Méthodes pour gérer les toasts
 */
export function useToast() {
  /**
   * Affiche une notification toast
   * @param {Object} options - Options de la notification
   * @param {string} options.title - Titre de la notification
   * @param {string} options.message - Message de la notification
   * @param {string} options.type - Type de notification: 'success', 'error', 'warning', 'info'
   * @param {number} options.duration - Durée d'affichage en ms (défaut: 5000ms)
   */
  const showToast = ({ title, message, type = 'info', duration = 5000 }) => {
    const id = toastIdCounter.value++
    
    // Ajouter le toast à la liste
    toasts.push({
      id,
      title,
      message,
      type,
      timestamp: Date.now()
    })
    
    // Supprimer automatiquement après la durée spécifiée
    setTimeout(() => {
      removeToast(id)
    }, duration)
    
    return id
  }
  
  /**
   * Supprime une notification par son ID
   * @param {number} id - ID de la notification à supprimer
   */
  const removeToast = (id) => {
    const index = toasts.findIndex(toast => toast.id === id)
    if (index !== -1) {
      toasts.splice(index, 1)
    }
  }
  
  /**
   * Supprime toutes les notifications
   */
  const clearToasts = () => {
    toasts.splice(0, toasts.length)
  }
  
  // Méthodes raccourcies pour les différents types de notifications
  const showSuccess = (message, title = 'Succès', duration) => {
    return showToast({ title, message, type: 'success', duration })
  }
  
  const showError = (message, title = 'Erreur', duration) => {
    return showToast({ title, message, type: 'error', duration })
  }
  
  const showWarning = (message, title = 'Attention', duration) => {
    return showToast({ title, message, type: 'warning', duration })
  }
  
  const showInfo = (message, title = 'Information', duration) => {
    return showToast({ title, message, type: 'info', duration })
  }
  
  return {
    toasts,
    showToast,
    removeToast,
    clearToasts,
    showSuccess,
    showError,
    showWarning,
    showInfo
  }
}
